import { useState, useEffect } from 'react';
import { useTenant } from '../../contexts/TenantContext';
import { useKidsStore } from '../../store/kidsStore';
import { toast } from 'sonner';

const COLORS = ['🔴', '🔵', '🟡', '🟢', '🟣', '🟠'];
const LEVELS = {
  neurona: { nombre: 'Neurona', icono: '🧠', intentos: 10, colores: 3, posiciones: 3 },
  sinapsis: { nombre: 'Sinapsis', icono: '⚡', intentos: 10, colores: 4, posiciones: 3 },
  cerebro: { nombre: 'Cerebro', icono: '🧩', intentos: 8, colores: 4, posiciones: 4 },
  genio: { nombre: 'Genio', icono: '💡', intentos: 8, colores: 5, posiciones: 4 },
  einstein: { nombre: 'Einstein', icono: '🎓', intentos: 6, colores: 6, posiciones: 4 }
};

export default function MastermindKidsGame() {
  const tenant = useTenant();
  const { selectedKid, registerProgress, registerPlayTime } = useKidsStore();

  const [nivel, setNivel] = useState<keyof typeof LEVELS>('neurona');
  const config = LEVELS[nivel];

  const [secret, setSecret] = useState<number[]>([]);
  const [currentGuess, setCurrentGuess] = useState<(number | null)[]>(Array(config.posiciones).fill(null));
  const [guesses, setGuesses] = useState<Array<{ guess: number[]; feedback: { blacks: number; whites: number } }>>([]);
  const [gameWon, setGameWon] = useState(false);
  const [gameLost, setGameLost] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [gameStarted, setGameStarted] = useState(false);

  useEffect(() => {
    startNewGame();
  }, [nivel]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (gameStarted && !gameWon && !gameLost) {
      interval = setInterval(() => setSeconds(s => s + 1), 1000);
    }
    return () => clearInterval(interval);
  }, [gameStarted, gameWon, gameLost]);

  const startNewGame = () => {
    const newSecret = Array.from({ length: config.posiciones }, () =>
      Math.floor(Math.random() * config.colores)
    );
    setSecret(newSecret);
    setCurrentGuess(Array(config.posiciones).fill(null));
    setGuesses([]);
    setGameWon(false);
    setGameLost(false);
    setSeconds(0);
    setGameStarted(false);
  };

  const handleColorClick = (colorIndex: number) => {
    if (gameWon || gameLost) return;
    if (!gameStarted) setGameStarted(true);

    const firstEmpty = currentGuess.findIndex(c => c === null);
    if (firstEmpty !== -1) {
      const newGuess = [...currentGuess];
      newGuess[firstEmpty] = colorIndex;
      setCurrentGuess(newGuess);
    }
  };

  const handleClear = () => {
    setCurrentGuess(Array(config.posiciones).fill(null));
  };

  const handleSubmit = async () => {
    if (currentGuess.some(c => c === null)) {
      toast.error('Completa todos los espacios');
      return;
    }

    const guess = currentGuess as number[];
    const feedback = getFeedback(guess, secret);

    setGuesses([...guesses, { guess, feedback }]);
    setCurrentGuess(Array(config.posiciones).fill(null));

    if (feedback.blacks === config.posiciones) {
      setGameWon(true);
      await saveProgress(true);
    } else if (guesses.length + 1 >= config.intentos) {
      setGameLost(true);
      await saveProgress(false);
    }
  };

  const getFeedback = (guess: number[], secret: number[]) => {
    let blacks = 0;
    let whites = 0;
    const secretCopy = [...secret];
    const guessCopy = [...guess];

    for (let i = 0; i < guessCopy.length; i++) {
      if (guessCopy[i] === secretCopy[i]) {
        blacks++;
        secretCopy[i] = -1;
        guessCopy[i] = -2;
      }
    }

    for (let i = 0; i < guessCopy.length; i++) {
      if (guessCopy[i] !== -2) {
        const index = secretCopy.indexOf(guessCopy[i]);
        if (index !== -1) {
          whites++;
          secretCopy[index] = -1;
        }
      }
    }

    return { blacks, whites };
  };

  const saveProgress = async (won: boolean) => {
    if (!selectedKid) return;

    try {
      await registerProgress(selectedKid.id, {
        juego_id: null,
        tipo_nivel: 'mastermind-kids',
        nivel_id: Object.keys(LEVELS).indexOf(nivel) + 1,
        tablero_id: null,
        tiempo: seconds,
        movimientos: guesses.length + 1
      });

      const minutosJugados = Math.ceil(seconds / 60);
      await registerPlayTime(selectedKid.id, minutosJugados);

      if (won) toast.success('¡Progreso guardado! 🎉');
    } catch (error) {
      console.error('Error al guardar:', error);
    }
  };

  return (
    <div
      className="h-full flex flex-col"
      style={{ background: tenant.theme.colors.gradients.main, fontFamily: tenant.theme.fonts.body.family }}
    >
      {/* Top bar */}
      <div className="bg-black/20 backdrop-blur-sm px-3 py-2 flex items-center gap-2 flex-wrap">
        <span className="text-white font-bold text-base whitespace-nowrap">🎯 Mastermind Kids</span>
        <div className="w-px h-5 bg-white/30 hidden sm:block" />
        <div className="flex gap-1 flex-wrap">
          {Object.entries(LEVELS).map(([key, level]) => (
            <button
              key={key}
              onClick={() => setNivel(key as keyof typeof LEVELS)}
              className={`px-2 py-0.5 rounded text-xs font-semibold transition-all ${
                nivel === key ? 'bg-white' : 'bg-white/20 text-white hover:bg-white/30'
              }`}
              style={nivel === key ? { color: tenant.theme.colors.primary } : {}}
            >
              {level.icono} {level.nombre}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <span className="text-white text-xs font-semibold">
          {guesses.length}/{config.intentos} · {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, '0')}
        </span>
        <button
          onClick={startNewGame}
          title="Nueva Partida"
          className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
        </button>
      </div>

      {/* Board */}
      <div className="flex-1 flex flex-col items-center justify-center p-3 pb-safe gap-4">
        <div className="w-full max-w-md space-y-2 max-h-52 overflow-y-auto">
          {guesses.map((item, idx) => (
            <div key={idx} className="flex items-center gap-2 p-2 bg-white/20 backdrop-blur-sm rounded-lg">
              <div className="flex gap-1">
                {item.guess.map((color, i) => (
                  <div key={i} className="text-3xl">{COLORS[color]}</div>
                ))}
              </div>
              <div className="ml-auto flex gap-1">
                {Array(item.feedback.blacks).fill(0).map((_, i) => (
                  <div key={i} className="w-3 h-3 rounded-full bg-black" />
                ))}
                {Array(item.feedback.whites).fill(0).map((_, i) => (
                  <div key={i} className="w-3 h-3 rounded-full bg-white border border-gray-400" />
                ))}
              </div>
            </div>
          ))}
        </div>

        {!gameWon && !gameLost && (
          <>
            <div className="flex gap-2 justify-center">
              {currentGuess.map((color, i) => (
                <div
                  key={i}
                  className="w-16 h-16 rounded-2xl flex items-center justify-center text-4xl border-4 border-white/50 bg-white/20"
                >
                  {color !== null ? COLORS[color] : '?'}
                </div>
              ))}
            </div>

            <div className="flex gap-2 justify-center">
              {COLORS.slice(0, config.colores).map((emoji, i) => (
                <button
                  key={i}
                  onClick={() => handleColorClick(i)}
                  className="w-14 h-14 text-4xl rounded-xl shadow-lg hover:scale-110 transition-all bg-white/20 hover:bg-white/30"
                >
                  {emoji}
                </button>
              ))}
            </div>

            <div className="flex gap-2 justify-center">
              <button
                onClick={handleClear}
                className="px-6 py-3 rounded-xl font-bold shadow-lg bg-white/20 hover:bg-white/30 text-white"
              >
                🗑️ Borrar
              </button>
              <button
                onClick={handleSubmit}
                className="px-6 py-3 rounded-xl font-bold shadow-lg text-white"
                style={{ background: tenant.theme.colors.primary }}
              >
                ✓ Enviar
              </button>
            </div>
          </>
        )}

        {(gameWon || gameLost) && (
          <div className="text-center bg-white/20 backdrop-blur-sm rounded-2xl p-8">
            <div className="text-6xl mb-4">{gameWon ? '🎉' : '😢'}</div>
            <h2 className="text-3xl font-bold mb-4 text-white">
              {gameWon ? '¡Ganaste!' : '¡Casi lo logras!'}
            </h2>
            {gameLost && (
              <div className="flex gap-2 justify-center mb-4">
                {secret.map((color, i) => (
                  <div key={i} className="text-4xl">{COLORS[color]}</div>
                ))}
              </div>
            )}
            <button
              onClick={startNewGame}
              className="px-8 py-4 rounded-2xl font-bold text-lg shadow-lg text-white"
              style={{ background: tenant.theme.colors.primary }}
            >
              🎮 Jugar de Nuevo
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
